import { supabase } from "@/utils/supabase/client";
import { toast } from "@/hooks/use-toast";
import { clearUserMetadataCache } from "@/utils/userMetadata";
import { sendSignupNotification } from "@/utils/slackNotifications";

// Safe logging wrapper
const safeLogger = {
  captureMessage: (message, options) => {
    console.log("📝 [Auth Store]", message, options);
  },
  captureException: (exception, options) => {
    console.error("❌ [Auth Store]", exception, options);
  },
};

const createAuthStore = (set, get) => {
  // Internal state for managing initialization and session restoration
  let authListener = null;
  let isInitializing = false;
  let initializationPromise = null;

  return {
    // Core auth state
    session: null,
    loading: false,
    error: null,
    isInitialized: false,
    lastSessionCheck: null,
    isSigningOut: false, // Track sign-out state

    // Cached user settings to prevent loss on session refresh
    cachedUserSettings: null,

    // Enhanced initialization with proper session restoration and timeout protection
    initialize: async () => {
      // Prevent multiple simultaneous initializations
      if (isInitializing && initializationPromise) {
        console.log(
          "🔄 [AuthStore] Initialization already in progress, waiting..."
        );
        return await initializationPromise;
      }

      isInitializing = true;
      console.log("🔄 [AuthStore] Starting enhanced initialization");

      // Add timeout protection for the entire initialization
      const initializationTimeout = setTimeout(() => {
        console.error("⏰ [AuthStore] Initialization timeout after 30 seconds");

        safeLogger.captureMessage("Auth initialization timeout", {
          level: "error",
          tags: {
            component: "AuthStore",
            issue_type: "initialization_timeout",
          },
          extra: {
            timeout: "30000ms",
            isInitializing,
            timestamp: new Date().toISOString(),
          },
        });

        // Force complete initialization to prevent infinite loading
        set({
          session: null,
          loading: false,
          isInitialized: true,
          error: "Initialization timeout",
          lastSessionCheck: Date.now(),
        });

        isInitializing = false;
        initializationPromise = null;
      }, 30000); // 30 second timeout

      initializationPromise = (async () => {
        try {
          console.log("🔄 [AuthStore] Setting loading state");
          set({ loading: true, error: null });

          // Step 1: Get current session from Supabase with timeout
          console.log("🔄 [AuthStore] Fetching session from Supabase");

          const sessionPromise = supabase.auth.getSession();
          const sessionTimeout = new Promise((_, reject) =>
            setTimeout(() => reject(new Error("Session fetch timeout")), 10000)
          );

          let sessionResult;
          try {
            sessionResult = await Promise.race([
              sessionPromise,
              sessionTimeout,
            ]);
          } catch (error) {
            console.error("❌ [AuthStore] Session fetch failed:", error);

            safeLogger.captureMessage("Session fetch timeout or failure", {
              level: "error",
              tags: {
                component: "AuthStore",
                issue_type: "session_fetch_timeout",
              },
              extra: {
                error: error.message,
                timestamp: new Date().toISOString(),
              },
            });

            // Continue with null session rather than hanging
            sessionResult = { data: { session: null }, error: null };
          }

          const {
            data: { session },
            error: sessionError,
          } = sessionResult;

          console.log("🔄 [AuthStore] Session fetch completed", {
            hasSession: !!session,
            hasError: !!sessionError,
            userId: session?.user?.id || null,
          });

          if (sessionError) {
            console.error("❌ [AuthStore] Session error:", sessionError);
            clearTimeout(initializationTimeout);
            set({
              session: null,
              loading: false,
              isInitialized: true,
              lastSessionCheck: Date.now(),
            });
            return;
          }

          console.log(
            `✅ [AuthStore] Session ${session ? "found" : "not found"}`
          );

          // If we have a session, ensure it has user settings
          if (session?.user?.id) {
            console.log("🔄 [AuthStore] Processing session with user");
            let finalSession = session;

            // Check if session already has user settings
            if (!session.user.user_settings) {
              console.log(
                "🔍 [AuthStore] Session missing user settings, fetching..."
              );

              // Try to use cached settings first
              const cachedSettings = get().cachedUserSettings;
              if (cachedSettings && cachedSettings.id === session.user.id) {
                console.log("✅ [AuthStore] Using cached user settings");
                finalSession = {
                  ...session,
                  user: {
                    ...session.user,
                    user_settings: cachedSettings,
                  },
                };
              } else {
                // Fetch fresh user settings with timeout
                console.log("🔄 [AuthStore] Fetching fresh user settings");
                try {
                  const userSettingsPromise = get().fetchUserSettings(session);
                  const userSettingsTimeout = new Promise((_, reject) =>
                    setTimeout(
                      () => reject(new Error("User settings fetch timeout")),
                      10000
                    )
                  );

                  const userSettings = await Promise.race([
                    userSettingsPromise,
                    userSettingsTimeout,
                  ]);

                  if (userSettings) {
                    console.log(
                      "✅ [AuthStore] User settings fetched successfully"
                    );
                    finalSession = {
                      ...session,
                      user: {
                        ...session.user,
                        user_settings: userSettings,
                      },
                    };
                    // Cache the settings
                    set({ cachedUserSettings: userSettings });
                  } else {
                    console.warn(
                      "⚠️ [AuthStore] User settings fetch returned null"
                    );
                  }
                } catch (error) {
                  console.error(
                    "❌ [AuthStore] User settings fetch error:",
                    error
                  );

                  safeLogger.captureMessage(
                    "User settings fetch timeout or failure",
                    {
                      level: "warning",
                      tags: {
                        component: "AuthStore",
                        issue_type: "user_settings_fetch_timeout",
                      },
                      extra: {
                        error: error.message,
                        userId: session.user.id,
                        timestamp: new Date().toISOString(),
                      },
                    }
                  );

                  // Continue with session without settings - don't block initialization
                }
              }
            } else {
              console.log("✅ [AuthStore] Session already has user settings");
              // Cache the existing settings
              set({ cachedUserSettings: session.user.user_settings });
            }

            console.log("🔄 [AuthStore] Setting final session state");
            set({
              session: finalSession,
              loading: false,
              isInitialized: true,
              lastSessionCheck: Date.now(),
            });

            // Set up enhanced auth listener
            console.log("🔄 [AuthStore] Setting up auth listener");
            if (authListener) {
              authListener.subscription?.unsubscribe();
            }

            const {
              data: { subscription },
            } = supabase.auth.onAuthStateChange(async (event, newSession) => {
              console.log(`🔔 [AuthStore] Auth event: ${event}`);

              // Handle different auth events
              switch (event) {
                case "SIGNED_OUT":
                  console.log("🚪 [AuthStore] User signed out");
                  set({
                    session: null,
                    isSigningOut: false,
                    cachedUserSettings: null,
                    lastSessionCheck: Date.now(),
                  });
                  break;

                case "SIGNED_IN":
                case "TOKEN_REFRESHED":
                  console.log(`🔑 [AuthStore] Processing ${event}`);

                  if (newSession?.user?.id) {
                    let enhancedSession = newSession;

                    // Always ensure user settings are present
                    if (!newSession.user.user_settings) {
                      console.log(
                        "🔍 [AuthStore] New session missing user settings"
                      );

                      // Try cached settings first
                      const cachedSettings = get().cachedUserSettings;
                      if (
                        cachedSettings &&
                        cachedSettings.id === newSession.user.id
                      ) {
                        console.log("✅ [AuthStore] Restoring from cache");
                        enhancedSession = {
                          ...newSession,
                          user: {
                            ...newSession.user,
                            user_settings: cachedSettings,
                          },
                        };
                      } else {
                        // Fetch fresh settings
                        try {
                          console.log(
                            "🔄 [AuthStore] Fetching fresh user settings"
                          );
                          const userSettings =
                            await get().fetchUserSettings(newSession);
                          if (userSettings) {
                            enhancedSession = {
                              ...newSession,
                              user: {
                                ...newSession.user,
                                user_settings: userSettings,
                              },
                            };
                            set({ cachedUserSettings: userSettings });
                          }
                        } catch (error) {
                          console.error(
                            "❌ [AuthStore] Failed to fetch user settings:",
                            error
                          );
                        }
                      }
                    } else {
                      // Update cache with existing settings
                      set({
                        cachedUserSettings: newSession.user.user_settings,
                      });
                    }

                    set({
                      session: enhancedSession,
                      lastSessionCheck: Date.now(),
                    });
                  } else {
                    set({
                      session: null,
                      cachedUserSettings: null,
                      lastSessionCheck: Date.now(),
                    });
                  }
                  break;

                case "INITIAL_SESSION":
                  // Handle initial session - this is often where the issue occurs
                  console.log("🏁 [AuthStore] Processing INITIAL_SESSION");

                  if (newSession?.user?.id) {
                    const currentSession = get().session;

                    // If we already have a session with settings, don't replace it
                    if (
                      currentSession?.user?.user_settings &&
                      currentSession.user.id === newSession.user.id
                    ) {
                      console.log(
                        "✅ [AuthStore] Keeping existing session with settings"
                      );
                      return;
                    }

                    // Otherwise, process like SIGNED_IN
                    let enhancedSession = newSession;

                    if (!newSession.user.user_settings) {
                      const cachedSettings = get().cachedUserSettings;
                      if (
                        cachedSettings &&
                        cachedSettings.id === newSession.user.id
                      ) {
                        enhancedSession = {
                          ...newSession,
                          user: {
                            ...newSession.user,
                            user_settings: cachedSettings,
                          },
                        };
                      } else {
                        try {
                          const userSettings =
                            await get().fetchUserSettings(newSession);
                          if (userSettings) {
                            enhancedSession = {
                              ...newSession,
                              user: {
                                ...newSession.user,
                                user_settings: userSettings,
                              },
                            };
                            set({ cachedUserSettings: userSettings });
                          }
                        } catch (error) {
                          console.error(
                            "❌ [AuthStore] Settings fetch failed:",
                            error
                          );
                        }
                      }
                    }

                    set({
                      session: enhancedSession,
                      lastSessionCheck: Date.now(),
                    });
                  }
                  break;

                default:
                  console.log(`🔔 [AuthStore] Unhandled event: ${event}`);
                  if (newSession) {
                    set({
                      session: newSession,
                      lastSessionCheck: Date.now(),
                    });
                  }
              }
            });

            authListener = { subscription };

            console.log("✅ [AuthStore] Enhanced initialization complete");
            clearTimeout(initializationTimeout);

            // Return cleanup function
            return () => {
              if (authListener?.subscription) {
                authListener.subscription.unsubscribe();
                authListener = null;
              }
            };
          } else {
            console.log(
              "🔄 [AuthStore] No session found, clearing auth and redirecting"
            );

            // Clear all auth-related data when no session is found
            await get().clearAllAuthData();

            // Clear timeout since we're completing initialization
            clearTimeout(initializationTimeout);

            set({
              session: null,
              loading: false,
              isInitialized: true,
              cachedUserSettings: null,
              lastSessionCheck: Date.now(),
            });

            // Only redirect if we're on a protected route that specifically requires authentication
            const currentPath =
              typeof window !== "undefined" ? window.location.pathname : "";
            const isProtectedRoute = currentPath.startsWith("/user");
            const isEditRoute = currentPath.includes("/edit");
            const isAuthRoute =
              currentPath.startsWith("/signin") ||
              currentPath.startsWith("/signup");
            const isPublicRoute =
              [
                "/",
                "/about",
                "/terms",
                "/privacy",
                "/featured",
                "/onboarding",
              ].includes(currentPath) || currentPath.startsWith("/featured");

            // Only redirect to signin if on a route that specifically requires authentication
            // Allow anonymous access to space view routes (but not edit routes)
            if (isProtectedRoute || isEditRoute) {
              console.log(
                "🔄 [AuthStore] Redirecting to signin due to missing session on protected/edit route"
              );

              safeLogger.captureMessage("Session timeout redirect", {
                level: "info",
                tags: {
                  component: "AuthStore",
                  issue_type: "session_timeout_redirect",
                },
                extra: {
                  currentPath,
                  isProtectedRoute,
                  isEditRoute,
                  isAuthRoute,
                  isPublicRoute,
                  timestamp: new Date().toISOString(),
                },
              });

              if (typeof window !== "undefined") {
                // Use replace to avoid back button issues
                window.location.replace("/signin");
                return; // Exit early since we're redirecting
              }
            }

            // Complete initialization successfully
            console.log(
              "✅ [AuthStore] Enhanced initialization complete (no session)"
            );
            return () => {
              // No cleanup needed since no auth listener was set up
            };
          }
        } catch (error) {
          console.error("❌ [AuthStore] Initialization failed:", error);
          clearTimeout(initializationTimeout);

          safeLogger.captureException(error, {
            tags: {
              component: "AuthStore",
              issue_type: "initialization_error",
            },
            extra: {
              timestamp: new Date().toISOString(),
            },
          });

          set({
            session: null,
            loading: false,
            error: error.message,
            isInitialized: true,
            lastSessionCheck: Date.now(),
          });
          throw error;
        } finally {
          isInitializing = false;
          initializationPromise = null;
        }
      })();

      return await initializationPromise;
    },

    // Enhanced user settings fetch with session parameter
    fetchUserSettings: async (sessionToUse = null) => {
      try {
        const session = sessionToUse || get().session;
        if (!session?.user?.id) {
          console.log("⚠️ [AuthStore] No session for user settings fetch");
          return null;
        }

        console.log(
          `🔍 [AuthStore] Fetching user settings for ${session.user.id.slice(0, 8)}...`
        );

        const { data, error } = await supabase
          .from("user_settings")
          .select("*")
          .eq("id", session.user.id)
          .single();

        if (error && error.code !== "PGRST116") {
          console.error("❌ [AuthStore] Error fetching user settings:", error);
          return null;
        }

        // If no settings exist, create them
        if (!data) {
          console.log("🆕 [AuthStore] Creating new user settings");
          const { data: newSettings, error: createError } = await supabase
            .from("user_settings")
            .insert({
              id: session.user.id,
              display_name: session.user.email?.split("@")[0] || "User",
              user_role: "user",
            })
            .select()
            .single();

          if (createError) {
            console.error(
              "❌ [AuthStore] Error creating user settings:",
              createError
            );
            return null;
          }

          console.log("✅ [AuthStore] Created new user settings");
          return newSettings;
        }

        console.log("✅ [AuthStore] User settings fetched successfully");
        return data;
      } catch (error) {
        console.error(
          "❌ [AuthStore] Unexpected error in fetchUserSettings:",
          error
        );
        return null;
      }
    },

    // Force session refresh - useful for tab visibility issues
    refreshSession: async () => {
      try {
        console.log("🔄 [AuthStore] Force refreshing session");
        set({ loading: true });

        const {
          data: { session },
          error,
        } = await supabase.auth.getSession();

        if (error) {
          console.error("❌ [AuthStore] Session refresh error:", error);
          set({ loading: false });
          return { success: false, error: error.message };
        }

        if (session?.user?.id) {
          // Ensure user settings are present
          let finalSession = session;

          if (!session.user.user_settings) {
            const userSettings = await get().fetchUserSettings(session);
            if (userSettings) {
              finalSession = {
                ...session,
                user: {
                  ...session.user,
                  user_settings: userSettings,
                },
              };
              set({ cachedUserSettings: userSettings });
            }
          } else {
            set({ cachedUserSettings: session.user.user_settings });
          }

          set({
            session: finalSession,
            loading: false,
            lastSessionCheck: Date.now(),
          });
        } else {
          set({
            session: null,
            loading: false,
            cachedUserSettings: null,
            lastSessionCheck: Date.now(),
          });
        }

        return { success: true };
      } catch (error) {
        console.error("❌ [AuthStore] Session refresh failed:", error);
        set({ loading: false });
        return { success: false, error: error.message };
      }
    },

    // Enhanced sign in with better session handling
    signIn: async (credentials) => {
      try {
        set({ loading: true, error: null });

        const { data, error } =
          await supabase.auth.signInWithPassword(credentials);

        if (error) {
          set({ loading: false });
          return { success: false, error: error.message };
        }

        // Ensure we have a valid session
        if (data.session?.user?.id) {
          // Fetch user settings to complete the session
          let finalSession = data.session;

          if (!data.session.user.user_settings) {
            console.log("🔍 [SignIn] Fetching user settings for session");
            const userSettings = await get().fetchUserSettings(data.session);
            if (userSettings) {
              finalSession = {
                ...data.session,
                user: {
                  ...data.session.user,
                  user_settings: userSettings,
                },
              };
              set({ cachedUserSettings: userSettings });
            }
          }

          // Update the session in the store
          set({
            session: finalSession,
            loading: false,
            lastSessionCheck: Date.now(),
          });

          return {
            success: true,
            session: finalSession,
            user: finalSession.user,
          };
        }

        set({ loading: false });
        return { success: false, error: "No session created" };
      } catch (error) {
        set({ loading: false });
        return { success: false, error: error.message };
      }
    },

    // Simple sign in with provider
    signInWithProvider: async (provider, options = {}) => {
      try {
        set({ loading: true, error: null });

        const { data, error } = await supabase.auth.signInWithOAuth({
          provider,
          options: {
            redirectTo:
              options.redirectTo || `${window.location.origin}/auth/callback`,
          },
        });

        if (error) {
          set({ loading: false });
          return { success: false, error: error.message };
        }

        return { success: true, data };
      } catch (error) {
        set({ loading: false });
        return { success: false, error: error.message };
      }
    },

    // Enhanced sign out
    signOut: async () => {
      try {
        console.log("🚪 [AuthStore] Signing out");
        set({ loading: true, isSigningOut: true });

        const { error } = await supabase.auth.signOut();

        if (error) {
          console.error("❌ [AuthStore] Sign out error:", error);
        }

        // Always clear local state
        set({
          session: null,
          loading: false,
          isSigningOut: false,
          cachedUserSettings: null,
          lastSessionCheck: Date.now(),
        });

        // Clear user metadata cache
        clearUserMetadataCache();

        return { success: true };
      } catch (error) {
        console.error("❌ [AuthStore] Sign out error:", error);
        set({
          session: null,
          loading: false,
          isSigningOut: false,
          cachedUserSettings: null,
          lastSessionCheck: Date.now(),
        });

        // Clear user metadata cache
        clearUserMetadataCache();

        return { success: false, error: error.message };
      }
    },

    // Simple sign up
    signUp: async ({ email, password }) => {
      try {
        set({ loading: true });

        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${process.env.NEXT_PUBLIC_APP_URL}/auth/callback?type=signup`,
          },
        });

        if (error) {
          set({ loading: false });
          return { success: false, error: error.message };
        }

        set({ loading: false });

        if (data?.session) {
          toast({
            title: "Success",
            description: "Account created successfully!",
            variant: "success",
          });
        } else {
          toast({
            title: "Success",
            description: "Please check your email to verify your account",
            variant: "success",
          });
        }

        // Send Slack notification for new signup (production only)
        if (
          data?.user &&
          process.env.NEXT_PUBLIC_APP_URL === "https://qensa.me"
        ) {
          try {
            await sendSignupNotification({
              email: data.user.email || email,
              userId: data.user.id,
              signupMethod: "Email",
              timestamp: new Date().toISOString(),
            });
          } catch (slackError) {
            console.error(
              "Failed to send Slack signup notification:",
              slackError
            );
            // Don't fail the signup process if Slack notification fails
          }
        } else if (data?.user) {
          console.log(
            "🔍 [Auth Store] New email signup detected, but skipping Slack notification (development environment)"
          );
        }

        return { success: true, session: data.session, user: data.user };
      } catch (error) {
        set({ loading: false });
        toast({
          title: "Error",
          description: error.message || "Failed to sign up",
          variant: "destructive",
        });
        return { success: false, error: error.message };
      }
    },

    // Check if email exists
    checkEmailExists: async (email) => {
      try {
        const response = await fetch("/api/auth/check-email", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        return { exists: data.exists, error: data.error };
      } catch (error) {
        console.error("❌ [AuthStore] Error checking email:", error);
        return { exists: false, error: error.message };
      }
    },

    // Check auth method
    checkAuthMethod: async (email) => {
      try {
        const response = await fetch("/api/auth/check-method", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        return { data, error: null };
      } catch (error) {
        console.error("❌ [AuthStore] Error checking auth method:", error);
        return { data: null, error: error.message };
      }
    },

    // Update user setting with cache management
    updateUserSetting: async (key, value) => {
      try {
        const session = get().session;
        if (!session?.user?.id) {
          return { success: false, error: "No user session" };
        }

        const { error } = await supabase
          .from("user_settings")
          .update({ [key]: value })
          .eq("id", session.user.id);

        if (error) {
          console.error(`❌ [AuthStore] Error updating ${key}:`, error);
          return { success: false, error: error.message };
        }

        // Update both session and cache
        const updatedUserSettings = {
          ...session.user.user_settings,
          [key]: value,
        };

        set({
          session: {
            ...session,
            user: {
              ...session.user,
              user_settings: updatedUserSettings,
            },
          },
          cachedUserSettings: updatedUserSettings,
        });

        return { success: true };
      } catch (error) {
        console.error(`❌ [AuthStore] Error updating ${key}:`, error);
        return { success: false, error: error.message };
      }
    },

    // Clear auth (alias for signOut)
    clearAuth: async () => {
      return await get().signOut();
    },

    // Utility to check if session is valid and has user settings
    validateSession: async () => {
      const session = get().session;

      if (!session?.user?.id) {
        return { valid: false, reason: "No session" };
      }

      if (!session.user.user_settings) {
        console.log(
          "⚠️ [AuthStore] Session missing user settings, attempting refresh"
        );
        const refreshResult = await get().refreshSession();

        if (refreshResult.success) {
          const updatedSession = get().session;
          return {
            valid: !!updatedSession?.user?.user_settings,
            reason: updatedSession?.user?.user_settings
              ? "Valid"
              : "Missing user settings",
          };
        }

        return { valid: false, reason: "Refresh failed" };
      }

      return { valid: true, reason: "Valid" };
    },

    // Clear all auth-related data
    clearAllAuthData: async () => {
      console.log("🚫 [AuthStore] Clearing all auth data");

      try {
        // Clear Supabase session
        await supabase.auth.signOut({ scope: "local" });

        // Clear localStorage items
        if (typeof window !== "undefined") {
          // Clear all auth-related localStorage items
          const keysToRemove = [];
          for (let i = 0; i < window.localStorage.length; i++) {
            const key = window.localStorage.key(i);
            if (
              key &&
              (key.includes("supabase") ||
                key.includes("auth") ||
                key.includes("session") ||
                key.startsWith("sb-") ||
                key === "qensa-store") // Clear the main store
            ) {
              keysToRemove.push(key);
            }
          }

          keysToRemove.forEach((key) => {
            window.localStorage.removeItem(key);
            console.log(`🗑️ [AuthStore] Cleared localStorage key: ${key}`);
          });

          // Clear sessionStorage as well
          const sessionKeysToRemove = [];
          for (let i = 0; i < window.sessionStorage.length; i++) {
            const key = window.sessionStorage.key(i);
            if (
              key &&
              (key.includes("supabase") ||
                key.includes("auth") ||
                key.includes("session") ||
                key.startsWith("sb-") ||
                key === "qensa-store")
            ) {
              sessionKeysToRemove.push(key);
            }
          }

          sessionKeysToRemove.forEach((key) => {
            window.sessionStorage.removeItem(key);
            console.log(`🗑️ [AuthStore] Cleared sessionStorage key: ${key}`);
          });

          // Clear cookies - specifically Supabase auth cookies
          get().clearAuthCookies();
        }

        // Clear any persisted store data
        const persistedStoreKey = "auth-store";
        if (typeof window !== "undefined") {
          window.localStorage.removeItem(persistedStoreKey);
          console.log(`🗑️ [AuthStore] Cleared persisted store`);
        }

        // Reset internal state
        set({
          session: null,
          loading: false,
          cachedUserSettings: null,
          isSigningOut: false,
          error: null,
          lastSessionCheck: Date.now(),
        });

        console.log("✅ [AuthStore] All auth data cleared successfully");
      } catch (error) {
        console.error("❌ [AuthStore] Error clearing auth data:", error);

        safeLogger.captureException(error, {
          tags: {
            component: "AuthStore",
            issue_type: "clear_auth_data_error",
          },
          extra: {
            timestamp: new Date().toISOString(),
          },
        });
      }
    },

    // Clear auth-related cookies
    clearAuthCookies: () => {
      if (typeof document !== "undefined") {
        console.log("🗑️ [AuthStore] Clearing auth cookies");

        // Get all cookies
        const cookies = document.cookie.split(";");

        cookies.forEach((cookie) => {
          const eqPos = cookie.indexOf("=");
          const name =
            eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();

          // Clear Supabase and auth-related cookies
          if (
            name.includes("sb-") ||
            name.includes("supabase") ||
            name.includes("auth") ||
            name.includes("session")
          ) {
            console.log(`🗑️ [AuthStore] Clearing cookie: ${name}`);

            // Clear for current domain
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;

            // Clear for current domain with leading dot
            const domain = window.location.hostname;
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain};`;
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${domain};`;

            // Clear for subdomains
            const domainParts = domain.split(".");
            if (domainParts.length > 1) {
              const rootDomain = domainParts.slice(-2).join(".");
              document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${rootDomain};`;
            }
          }
        });

        console.log("✅ [AuthStore] Auth cookies cleared");
      }
    },
  };
};

export default createAuthStore;
